///
/// EarthBrowser
///
/// Created by Matt Giger on 8/19/2024
/// Copyright (c) 2024 Geodata Labs
///

import { UserDatasetActivity } from "@/earthbrowser/activities/DatasetActivity/DatasetActivity"
import { GroundOverlayActivity } from "@/earthbrowser/dataset-distribution/ground-overlay/GroundOverlayActivity"
import { EarthActivity } from "@/earthbrowser/activities/EarthActivity/EarthActivity"
import { ViewThumbnailStore } from "@/earthbrowser/camera-director/ViewThumbnailStore"
import { CacheTable } from "@/earthbrowser/database/tables/CacheTable"
import { AuroraDatasetLayer } from "@/earthbrowser/dataset-distribution/aurora/AuroraDatasetLayer"
import { BuoysDatasetLayer } from "@/earthbrowser/dataset-distribution/buoys/BuoysDatasetLayer"
import { CloudsDatasetLayer } from "@/earthbrowser/dataset-distribution/clouds/CloudsDatasetLayer"
import { DatasetLayerManager } from "@/earthbrowser/dataset-distribution/DatasetLayerManager"
import { EarthquakesDatasetLayer } from "@/earthbrowser/dataset-distribution/earthquakes/EarthquakesDatasetLayer"
import { QuakeHistoryDatasetLayer } from "@/earthbrowser/dataset-distribution/earthquakes/history/QuakeHistoryDatasetLayer"
import { Slab2DatasetLayer } from "@/earthbrowser/dataset-distribution/earthquakes/slab2/Slab2DatasetLayer"
import { HurricanesDatasetLayer } from "@/earthbrowser/dataset-distribution/hurricanes/HurricanesDatasetLayer"
import { ImergDatasetLayer } from "@/earthbrowser/dataset-distribution/imerg/ImergDatasetLayer"
import { LocationsDatasetLayer } from "@/earthbrowser/dataset-distribution/locations/LocationsDatasetLayer"
import { RadarDatasetLayer } from "@/earthbrowser/dataset-distribution/radar/RadarDatasetLayer"
import { RadarForecastDatasetLayer } from "@/earthbrowser/dataset-distribution/radar-forecast/RadarForecastDatasetLayer"
import { CloudForecastDatasetLayer } from "@/earthbrowser/dataset-distribution/cloud-forecast/CloudForecastDatasetLayer"
import { SstDatasetLayer } from "@/earthbrowser/dataset-distribution/sst/SstDatasetLayer"
// MRMS CONUS radar disabled — re-enable by uncommenting registration below
// import { MrmsDatasetLayer } from "@/earthbrowser/dataset-distribution/mrms/MrmsDatasetLayer"
import { NexradDatasetLayer } from "@/earthbrowser/dataset-distribution/nexrad/NexradDatasetLayer"
import { PlacemarksLayer } from "@/earthbrowser/dataset-distribution/placemarks/PlacemarksLayer"
import { StoryLayer } from "@/earthbrowser/dataset-distribution/stories/StoryLayer"
import { sharedEmberDomeBatch } from "@/earthbrowser/entities/EmberDome/EmberDomeBatch"
import { AlertsDatasetLayer } from "@/earthbrowser/dataset-distribution/alerts/AlertsDatasetLayer"
import { SatelliteDatasetLayer } from "@/earthbrowser/dataset-distribution/satellites/SatelliteDatasetLayer"
import { SynopticDatasetLayer } from "@/earthbrowser/dataset-distribution/synoptic/SynopticDatasetLayer"
import { VolcanoesDatasetLayer } from "@/earthbrowser/dataset-distribution/volcanoes/VolcanoesDatasetLayer"
import { sharedWebcamBadgeBatch } from "@/earthbrowser/dataset-distribution/webcams/WebcamBadgeBatch"
import { sharedWebcamChipBatch } from "@/earthbrowser/dataset-distribution/webcams/WebcamChipBatch"
import { sharedWebcamGlowBatch } from "@/earthbrowser/dataset-distribution/webcams/WebcamGlowBatch"
import { WebcamsDatasetLayer } from "@/earthbrowser/dataset-distribution/webcams/WebcamsDatasetLayer"
import { WildfiresDatasetLayer } from "@/earthbrowser/dataset-distribution/wildfires/WildfiresDatasetLayer"
import { FocusedObjectCallout } from "@/earthbrowser/design-system/FocusedObjectCallout"
import { EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { installDebugHooks } from "@/earthbrowser/utils/debugHooks"
import { applyMobileEngineTuning, installVisibilityLifecycle } from "@/earthbrowser/utils/engineLifecycle"
import { startMemoryHeartbeat } from "@/earthbrowser/utils/MemoryLog"
import { isMobile } from "@/earthbrowser/utils/platform"
import { waiter } from "@/earthbrowser/utils/waiter"
import { clearTargetFromHistory, parseStoryTarget, parseTarget } from "@/utils/navigationHistory"
import { cameraPoseFromParams } from "@/utils/cameraPoseUrl"
import { isInfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import { setLayerManager } from "@/redux/datasetSceneSlice"
import { sendCameraCommand, sendDatasetAction, setAnimatedCameraTilt, setCameraBearing, setCameraTilt, type CameraCommand } from "@/redux/earthBrowserSlice"
import type { DatasetAction, DatasetType } from "@/types/DatasetElement"
import { reduxActionBridge } from "@/redux/ReduxActionBridge"
import { appStore } from "@/redux/store"
import { closeInfoWindow, openInfoWindow } from "@/redux/userInterfaceSlice"
import { ActivityBase } from "@stovokor/engine/ActivityBase"
import { Stovokor } from "@stovokor/Stovokor"
import { KernelTimerTask } from "@stovokor/tasks/TimerTask"

export class EarthBrowser extends Stovokor {
	private disposeLifecycle: () => void

	constructor(canvas: HTMLCanvasElement) {
		const context = new EarthBrowserContext(canvas)
		super(context)

		// Phones: lower idle draw rate, and a lower drawing-buffer cap on
		// Android; pause while the page / app is in the background
		applyMobileEngineTuning(this, context)
		this.disposeLifecycle = installVisibilityLifecycle(this)

		// Saved-view thumbnails must be captured while the frame's drawing
		// buffer is valid (no preserveDrawingBuffer on the GL context)
		this.onPostRender = () => ViewThumbnailStore.shared.capturePending(canvas)

		this.setBaseActivity(new EarthBrowserActivity(context, this))
	}

	override destroy(): void {
		this.disposeLifecycle()
		super.destroy()
	}
}

class EarthBrowserActivity extends ActivityBase {
	private earthActivity: EarthActivity
	private earthLoadWaiter = new waiter()

	private datasetLayerManager!: DatasetLayerManager
	private focusedObjectCallout?: FocusedObjectCallout
	private hoveredObjectCallout?: FocusedObjectCallout
	private reduxUnsubscribers: (() => void)[] = []

	constructor(context: EarthBrowserContext, engine: Stovokor) {
		super(context)

		this.earthActivity = new EarthActivity(context)

		// Create the DatasetLayerManager (central coordinator for all dataset layers)
		this.datasetLayerManager = new DatasetLayerManager(context, engine.world)

		// Register all dataset layers
		const cloudsLayer = new CloudsDatasetLayer(context, this.earthActivity.earth)
		this.datasetLayerManager.registerLayer(cloudsLayer)
		// RRFS simulated satellite: extends the clouds past the newest observation
		this.datasetLayerManager.registerLayer(new CloudForecastDatasetLayer(context, cloudsLayer))
		this.datasetLayerManager.registerLayer(new AuroraDatasetLayer(context))
		this.datasetLayerManager.registerLayer(new SatelliteDatasetLayer(context))

		this.datasetLayerManager.registerLayer(new LocationsDatasetLayer(context, engine.world))
		this.datasetLayerManager.registerLayer(new VolcanoesDatasetLayer(context))
		this.datasetLayerManager.registerLayer(new EarthquakesDatasetLayer(context))
		this.datasetLayerManager.registerLayer(new QuakeHistoryDatasetLayer(context))
		this.datasetLayerManager.registerLayer(new Slab2DatasetLayer(context))
		this.datasetLayerManager.registerLayer(new NexradDatasetLayer(context))
		// this.datasetLayerManager.registerLayer(new MrmsDatasetLayer(context, this.earthActivity.earth))
		this.datasetLayerManager.registerLayer(new ImergDatasetLayer(context, this.earthActivity.earth))
		const radarLayer = new RadarDatasetLayer(context, this.earthActivity.earth)
		this.datasetLayerManager.registerLayer(radarLayer)
		// RRFS / REFS forecast: extends the radar past the newest observation
		this.datasetLayerManager.registerLayer(new RadarForecastDatasetLayer(context, this.earthActivity.earth, radarLayer))
		this.datasetLayerManager.registerLayer(new SstDatasetLayer(context, this.earthActivity.earth))
		this.datasetLayerManager.registerLayer(new BuoysDatasetLayer(context))
		this.datasetLayerManager.registerLayer(new WebcamsDatasetLayer(context))
		this.datasetLayerManager.registerLayer(new WildfiresDatasetLayer(context))
		this.datasetLayerManager.registerLayer(new HurricanesDatasetLayer(context))
		this.datasetLayerManager.registerLayer(new AlertsDatasetLayer(context))
		this.datasetLayerManager.registerLayer(new PlacemarksLayer(context))

		// Story annotations render from redux (active/edited story), no
		// DB sync and no dataset toggle - visibility follows playback state
		engine.world.addNode(new StoryLayer(context))

		// Every marker dome in the scene draws through this one instanced
		// batch; the dome nodes under the layers above only feed it
		engine.world.addNode(sharedEmberDomeBatch(context))
		// Webcam thumbnail chips and cluster badges draw the same way (fed by
		// WebcamCloudNode); the badge batch orders itself after the chips
		engine.world.addNode(sharedWebcamGlowBatch(context))
		engine.world.addNode(sharedWebcamChipBatch(context))
		engine.world.addNode(sharedWebcamBadgeBatch(context))

		// Synoptic layer computes from WeatherGridService directly (not CDN sync)
		const synopticLayer = new SynopticDatasetLayer(context)
		engine.world.addNode(synopticLayer)

		// Synoptic isn't a managed DatasetLayer, so surface its time range to
		// the scrubber through an external provider
		this.datasetLayerManager.registerTimeRangeProvider("synoptic", () => {
			if (!synopticLayer.visible) return null
			const range = synopticLayer.getAvailableTimeRange()
			return range ? { ...range, ...synopticLayer.getTimeLaneDetail() } : null
		})
		this.datasetLayerManager.registerLoadStateProvider("synoptic", () => {
			const state = synopticLayer.getLoadState()
			return state ? { ...state, visible: synopticLayer.visible } : null
		})

		appStore.dispatch(setLayerManager(this.datasetLayerManager))
	}

	public start(): void {

		// Startup memory heartbeat: brackets the allocation that kills
		// memory-constrained mobile tabs, where no error is ever thrown
		startMemoryHeartbeat()

		const context = this.context as EarthBrowserContext
		this.addActivity(this.earthActivity)

		// Sync all layers in background, then start polling for layers that need it.
		// syncAndStartPolling self-heals on transient failures (network/db cold start)
		// by retrying with backoff, so a single blip no longer requires a page reload.
		void this.datasetLayerManager.syncAndStartPolling()

		// Initialize the SelectionCoordinator with the engine for centralized UI management
		context.selectionCoordinator.initialize(this.engine)
		this.focusedObjectCallout = new FocusedObjectCallout(context, this)
		this.hoveredObjectCallout = new FocusedObjectCallout(context, this, { ownsAnchoredReactPanel: false })
		context.focusedObjectCallout = this.focusedObjectCallout
		context.hoveredObjectCallout = this.hoveredObjectCallout


		this.addTimer(0.4, 0, () => {
			this.addActivity(new UserDatasetActivity(context))
			this.addActivity(new GroundOverlayActivity(context, this.earthActivity.earth))
		})

		// Wait for the earth to be ready before showing our load in progress
		this.engine.world.visible = false
		this.earthLoadWaiter
			.wait(0.8, () => {
				return this.earthActivity.renderReady
			})
			.finally(() => this.engine.world.visible = true)

		// Purge the cache every minute (after a 30 second delay)
		const purgeTask = new KernelTimerTask(30, 60, () => { void CacheTable.purge() })
		this.engine.taskManager.addTimerTask(purgeTask)


		// ========== Camera Control Commands (from Redux actions) ==========
		// Subscribe directly to Redux actions via the action bridge
		// Use immediate (non-animated) commands for slider input to avoid feedback loops
		//
		// Every one of these is user-initiated (HUD buttons, keyboard
		// shortcuts), so each first interrupts automated motion: an active
		// orbit rewrites the camera pose every frame and would otherwise
		// swallow the command
		const cameraDirector = this.earthActivity.cameraDirector

		this.reduxUnsubscribers.push(
			reduxActionBridge.subscribe<number>(setCameraBearing.type, (payload: number) => {
				cameraDirector.interruptForUserInput()
				this.context.focusManager.sendCommand("camera/setBearingImmediate", payload)
			})
		)

		this.reduxUnsubscribers.push(
			reduxActionBridge.subscribe<number>(setCameraTilt.type, (payload: number) => {
				cameraDirector.interruptForUserInput()
				this.context.focusManager.sendCommand("camera/setTiltImmediate", payload)
			})
		)

		this.reduxUnsubscribers.push(
			reduxActionBridge.subscribe<number>(setAnimatedCameraTilt.type, (payload: number) => {
				cameraDirector.interruptForUserInput()
				this.context.focusManager.sendCommand("camera/setTilt", payload)
			})
		)

		this.reduxUnsubscribers.push(
			reduxActionBridge.subscribe<CameraCommand>(sendCameraCommand.type, (payload: CameraCommand) => {
				// Saved-view flights go to the CameraDirector (it handles
				// releasing entity focus first); everything else routes to
				// the focused node's command handler
				if (payload.command === "view/flyTo") {
					const data = payload.data as { viewId?: string } | undefined
					if (data?.viewId) {
						cameraDirector.flyToView(data.viewId)
					}
					return
				}
				if (payload.command === "orbit/toggle") {
					cameraDirector.toggleOrbit()
					return
				}
				if (payload.command.startsWith("story/")) {
					cameraDirector.handleStoryCommand(payload.command, payload.data)
					return
				}
				if (payload.command === "camera/stopMotion") {
					cameraDirector.stopAllMotion()
					return
				}
				if (payload.command.startsWith("location/")) {
					cameraDirector.handleLocationCommand(payload.command)
					return
				}
				// zoom/in, zoom/out, zoom/set, flyTo/location, compass/reset:
				// direct view manipulation, leaves orbit/flight first
				cameraDirector.interruptForUserInput()
				this.context.focusManager.sendCommand(payload.command, payload.data)
			})
		)

		// ========== Dataset Selection Commands (from Redux actions) ==========
		// Handle selectId and selectCoords actions by focusing on the appropriate scene object
		this.reduxUnsubscribers.push(
			reduxActionBridge.subscribe<DatasetAction>(sendDatasetAction.type, (payload: DatasetAction) => {
				this.handleDatasetSelection(payload)
			})
		)

		// Dev-only window.__ebDebug camera hooks for tooling/LLM agents
		installDebugHooks(this.earthActivity.cameraDirector, context, this.engine)

		// ========== Info Panel Auto-Population ==========
		// Focus change fires at request time, before the camera flight; the
		// full info panel waits for the settled event so it isn't sliding
		// around (and competing with the globe) during the flight. While the
		// flight runs, the focused callout shows its compact preview card
		// tracking the target - the same card the user was just hovering -
		// so selection reads as continuous. Immediate focus changes (no
		// flight) settle synchronously before this callback, so the panel is
		// already up by the time it runs.
		const focusManager = this.context.focusManager
		const unsubscribeFocus = focusManager.onFocusChange((newFocus, _oldFocus) => {
			if (newFocus && isInfoPanelProvider(newFocus)) {
				const panelData = newFocus.getInfoPanelData()
				if (!panelData) {
					this.focusedObjectCallout?.hide()
					return
				}
				this.hoveredObjectCallout?.hide()
				if (focusManager.isTransitioning()) {
					// Drop any previous entity's panel/callout; the new one is
					// presented on settle. The preview is an anchored engine
					// card, which on phones would sit over the globe, so the
					// bottom sheet simply arrives with the camera there
					this.focusedObjectCallout?.hide()
					appStore.dispatch(closeInfoWindow())
					if (!isMobile()) {
						this.focusedObjectCallout?.previewFor(newFocus)
					}
				}
			} else {
				// Focus released or went to non-InfoPanelProvider (e.g., Earth) - close the info panel
				this.hoveredObjectCallout?.hide()
				this.focusedObjectCallout?.hide()
				appStore.dispatch(closeInfoWindow())

				// Remove the target param so the URL stops claiming an entity is
				// focused (otherwise refresh/share/bookmark captures stale state).
				// No-op if the URL has no target.
				clearTargetFromHistory()
			}
		})
		this.reduxUnsubscribers.push(unsubscribeFocus)

		const unsubscribeSettled = focusManager.onFocusSettled(node => {
			// Stale settle (focus moved on while the flight ran) or a node
			// without a panel: nothing to present
			if (focusManager.getFocusedNode() !== node || !isInfoPanelProvider(node)) {
				return
			}
			const panelData = node.getInfoPanelData()
			if (!panelData) {
				return
			}
			this.focusedObjectCallout?.showFor(node)
			appStore.dispatch(openInfoWindow({
				title: panelData.title,
				subtitle: panelData.subtitle,
				theme: panelData.theme,
				contentType: panelData.contentType,
				contentData: panelData.contentData
			}))
		})
		this.reduxUnsubscribers.push(unsubscribeSettled)

		// Launch deep-links (?target=sat-..., geoid-..., coords@...): the React
		// side dispatches the URL target on mount, which is long before the
		// action-bridge subscription exists and the bridge has no replay. Now
		// that the layers are registered and we're subscribed, read the target
		// directly. Must run after the focus-change subscription above:
		// redux-fed layers (placemarks) resolve their node synchronously, so
		// the focus notification would otherwise fire with no subscriber and
		// the info panel would never open for the launch target.
		this.focusInitialTarget()
	}

	override destroy(): void {
		this.hoveredObjectCallout?.destroy()
		this.hoveredObjectCallout = undefined
		this.focusedObjectCallout?.destroy()
		this.focusedObjectCallout = undefined
		const context = this.context as EarthBrowserContext
		context.focusedObjectCallout = undefined
		context.hoveredObjectCallout = undefined
		this.reduxUnsubscribers.forEach(unsubscribe => unsubscribe())
		this.reduxUnsubscribers = []
		super.destroy()
	}

	/**
	 * Focus a scene object (selectId) or coordinates (selectCoords)
	 */
	private handleDatasetSelection(payload: DatasetAction): void {
		if (payload.action === "deselect") {
			// URL target was removed (e.g. browser Back to the pre-focus entry):
			// release focus so the view matches the URL. Preserve the camera to
			// avoid a surprise flight; controls just return to Earth navigation.
			// releaseFocus is a no-op if nothing is focused.
			this.context.focusManager.releaseFocus({ preserveCurrentState: true })
		} else if (payload.action === "selectId" && payload.id) {
			const layerName = this.datasetTypeToLayerName(payload.type)
			if (layerName) {
				this.datasetLayerManager.focusSceneObject(layerName, payload.id)
			}
		} else if (payload.action === "selectCoords" && payload.lat !== undefined && payload.lon !== undefined) {
			// Coordinate-based selection (OGC features, temporary locations,
			// placemark member fly-to): route through the focused handler's
			// command chain, which ends at Earth's flyTo/location. (The old
			// "camera/focusOnCoords" command existed nowhere and was a no-op.)
			// Leave any orbit first or it overwrites the flight each frame
			this.earthActivity.cameraDirector.interruptForUserInput()
			this.context.focusManager.sendCommand("flyTo/location", {
				lat: payload.lat,
				lon: payload.lon,
			})
		}
	}

	/**
	 * Handle ?target= / camera-pose deep-links present in the URL at
	 * engine startup. A target wins over a camera pose: the focus
	 * transition frames the entity itself, so the pose is only used for
	 * plain shared views (?layers= is applied by PageContent on mount).
	 */
	private focusInitialTarget(): void {
		const params = new URL(window.location.href).searchParams
		const target = params.get('target')

		if (!target) {
			this.applyInitialCameraPose(params)
			return
		}

		// Story deep link: auto-start playback at the linked stop
		const storyTarget = parseStoryTarget(target)
		if (storyTarget) {
			this.earthActivity.cameraDirector.handleStoryCommand("story/play", {
				storyId: storyTarget.storyId,
				stopIndex: storyTarget.stopIndex,
			})
			return
		}

		const parsed = parseTarget(target)
		if (!parsed) return

		if (parsed.kind === 'coords') {
			this.handleDatasetSelection({
				type: 'location',
				action: "selectCoords",
				id: '0',
				lat: parsed.lat,
				lon: parsed.lon,
				name: parsed.name
			})
		} else {
			this.handleDatasetSelection({ type: parsed.type, action: "selectId", id: parsed.id })
		}
	}

	/**
	 * Apply a launch camera-pose deep link — the human-readable
	 * ?lat=&lon=&alt=&tilt=&bearing= form or the legacy raw
	 * ?camera=radius,theta,phi,tilt,bearing — placing the camera at the
	 * shared pose immediately (no flight from the recipient's persisted
	 * position).
	 */
	private applyInitialCameraPose(params: URLSearchParams): void {
		const pose = cameraPoseFromParams(params)
		if (!pose) return

		this.earthActivity.cameraDirector.setPoseImmediate(pose)
	}

	/**
	 * Map DatasetType to the corresponding layer name in DatasetLayerManager
	 */
	private datasetTypeToLayerName(type: DatasetType): string | null {
		switch (type) {
			case "satellite": return "satellites"
			case "earthquake": return "earthquakes"
			case "volcano": return "volcanoes"
			case "location": return "locations"
			case "buoy": return "buoys"
			case "webcam": return "webcams"
			case "wildfire": return "wildfires"
			case "hurricane": return "hurricanes"
			case "alert": return "alerts"
			case "placemark": return "placemarks"
			case "nexrad": return "nexrad"
			default: return null
		}
	}
}
